/**
 * Hafif sayfa + API performans izleme (opt-in).
 * Açma: localStorage.setItem('dekosta_perf_diag','1') veya REACT_APP_PERF_DIAGNOSTICS=true
 *
 * Ölçüm: giriş yap → sayfayı aç → unmount’ta POST /me/perf-log (first_data_ms, api_calls[]).
 * Karşılaştırma: aynı cihazda cold (gizli sekme + boş cache / hard reload) vs warm (tekrar gezinme).
 * Backend süre: DEKOSTA_API_TIMING_LOG=1 ile sunucu loglarında api_timing satırları.
 */

import axios from 'axios';
import { getApiBase } from './apiBase';

const API_BASE = getApiBase() + '/api';
const MAX_API_ROWS = 40;
const PATH_MAX = 160;

let session = null;
let interceptorIds = { req: null, res: null };

function nowMs() {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}

export function isPerfDiagnosticsEnabled() {
  try {
    if (process.env.REACT_APP_PERF_DIAGNOSTICS === '1' || process.env.REACT_APP_PERF_DIAGNOSTICS === 'true') {
      return true;
    }
    return typeof localStorage !== 'undefined' && localStorage.getItem('dekosta_perf_diag') === '1';
  } catch {
    return false;
  }
}

/** route params: yalnızca güvenli anahtarlar, kısa değerler */
export function sanitizeRouteParams(params) {
  if (!params || typeof params !== 'object') return {};
  const allow = new Set(['itemId', 'characterId', 'collectionId']);
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (!allow.has(k) || v == null) continue;
    const s = String(v).slice(0, 48);
    if (/^[a-zA-Z0-9_-]+$/.test(s)) out[k] = s;
  }
  return out;
}

function normalizeApiPath(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    let p = url;
    const idx = p.indexOf('/api');
    if (idx >= 0) p = p.slice(idx);
    const q = p.indexOf('?');
    if (q >= 0) p = p.slice(0, q);
    return p.slice(0, PATH_MAX);
  } catch {
    return '';
  }
}

export function logApiPerf(path, method, durationMs, status) {
  if (!session || !isPerfDiagnosticsEnabled()) return;
  const p = (path || '').slice(0, PATH_MAX);
  if (!p) return;
  session.api_calls.push({
    path: p,
    method: (method || 'GET').toUpperCase().slice(0, 8),
    duration_ms: Math.min(120000, Math.max(0, Math.round(durationMs))),
    status: typeof status === 'number' ? status : 0,
  });
  if (session.api_calls.length > MAX_API_ROWS) {
    session.api_calls.shift();
  }
}

/**
 * @param {string} pageKey
 * @param {Record<string,string>} routeParams
 */
export function startPagePerf(pageKey, routeParams = {}) {
  if (!isPerfDiagnosticsEnabled()) return;
  session = {
    page_key: String(pageKey || 'unknown').slice(0, 64),
    enter_ts: nowMs(),
    route_params: sanitizeRouteParams(routeParams),
    api_calls: [],
    first_data_ms: null,
    flushed: false,
  };
}

export function markPagePerfDataLoaded() {
  if (!session || session.first_data_ms != null) return;
  session.first_data_ms = Math.round(nowMs() - session.enter_ts);
}

export function finishPagePerf() {
  if (!session || session.flushed) return null;
  const total = Math.round(nowMs() - session.enter_ts);
  const payload = {
    page_key: session.page_key,
    total_load_ms: Math.min(120000, Math.max(0, total)),
    first_data_ms: session.first_data_ms != null ? Math.min(120000, session.first_data_ms) : null,
    api_calls: [...session.api_calls],
    created_at_client: new Date().toISOString(),
    route_params: session.route_params,
  };
  return payload;
}

export async function finishPagePerfAndFlush() {
  const payload = finishPagePerf();
  if (!payload || session?.flushed) return;
  session.flushed = true;
  try {
    await axios.post(`${API_BASE}/me/perf-log`, payload, {
      withCredentials: true,
      timeout: 8000,
      headers: { 'Content-Type': 'application/json' },
    });
  } catch {
    /* sessiz — tanı hataya düşürmez */
  }
  session = null;
}

export function clearPagePerfSession() {
  session = null;
}

export function initAxiosPerfTracking() {
  if (!isPerfDiagnosticsEnabled()) return () => {};

  if (interceptorIds.req != null) {
    try {
      axios.interceptors.request.eject(interceptorIds.req);
      axios.interceptors.response.eject(interceptorIds.res);
    } catch {
      /* */
    }
    interceptorIds = { req: null, res: null };
  }

  interceptorIds.req = axios.interceptors.request.use((config) => {
    config.metadata = { ...config.metadata, perfStart: nowMs() };
    return config;
  });

  interceptorIds.res = axios.interceptors.response.use(
    (res) => {
      const start = res.config?.metadata?.perfStart;
      if (start != null && res.config?.url) {
        const path = normalizeApiPath(res.config.url);
        const dur = nowMs() - start;
        logApiPerf(path, res.config.method, dur, res.status);
      }
      return res;
    },
    (err) => {
      const cfg = err.config;
      const start = cfg?.metadata?.perfStart;
      if (start != null && cfg?.url) {
        const path = normalizeApiPath(cfg.url);
        const dur = nowMs() - start;
        const st = err.response?.status || 0;
        logApiPerf(path, cfg.method, dur, st);
      }
      return Promise.reject(err);
    }
  );

  return () => {
    try {
      if (interceptorIds.req != null) axios.interceptors.request.eject(interceptorIds.req);
      if (interceptorIds.res != null) axios.interceptors.response.eject(interceptorIds.res);
    } catch {
      /* */
    }
    interceptorIds = { req: null, res: null };
  };
}
